import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './Auth.css';

const ShortURLLanding = ({ apiUrl, onLogin }) => {
  const [searchParams] = useSearchParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const code = searchParams.get('code');

  // After signing in (new or existing account) via this link, carry the
  // service and coupon straight into the booking flow so nothing has to be
  // re-entered manually.
  const goToBooking = () => {
    navigate('/booking', { state: { serviceId: service.service_id, couponCode: service.coupon_code || undefined } });
  };

  useEffect(() => {
    if (code) {
      fetchServiceFromCode();
    } else {
      setError('Invalid registration link');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // A client who's already logged in (e.g. opening this link on a device
  // where they're still signed in) doesn't need the register/login card -
  // send them straight into the booking flow with the service and coupon.
  useEffect(() => {
    if (service && localStorage.getItem('token')) {
      goToBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  const fetchServiceFromCode = async () => {
    try {
      const response = await axios.get(`${apiUrl}/short-urls/${code}/service`);
      setService(response.data);
    } catch (err) {
      setError('Service not found or link expired');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAndBook = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.name) {
      setError('Please fill in all fields');
      return;
    }
    if (!acceptedTerms) {
      setError('You must agree to the Terms & Conditions and Privacy Policy');
      return;
    }

    try {
      setRegistering(true);
      setError(null);
      const response = await axios.post(`${apiUrl}/auth/register-via-url`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        accepted_terms: acceptedTerms,
        short_url_code: code
      });

      onLogin({ token: response.data.token, user: response.data.user });
      goToBooking();
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoggingIn(true);
    try {
      const response = await axios.post(`${apiUrl}/auth/login`, loginData);
      onLogin({ token: response.data.token, user: response.data.user });
      goToBooking();
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  if (loading) {
    return <div className="auth-container"><p style={{ color: 'white' }}>Loading service details...</p></div>;
  }

  if (error && !service) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#dc2626' }}>Oops!</h1>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" style={{ alignItems: 'flex-start', paddingTop: 40 }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <div className="auth-card" style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
          <div style={{ background: 'linear-gradient(135deg, #c2366f, #7d1f47)', padding: 28, color: 'white' }}>
            <h1 style={{ color: 'white', marginBottom: 6 }}>{service.service_name}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {service.practitioner_photo && (
                <img
                  src={`${apiUrl}/uploads/profiles/${service.practitioner_photo}`}
                  alt={service.practitioner_name}
                  style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.6)' }}
                />
              )}
              <p style={{ opacity: 0.9, margin: 0 }}>with {service.practitioner_name}</p>
            </div>
          </div>
          <div style={{ padding: 28 }}>
            {service.practitioner_bio && (
              <p className="text-muted" style={{ marginBottom: 16, fontStyle: 'italic' }}>{service.practitioner_bio}</p>
            )}
            <p className="text-muted" style={{ marginBottom: 20 }}>{service.service_description}</p>
            {service.coupon_code && (
              <div style={{ background: '#fbe9f0', color: '#7d1f47', padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: '0.9rem', fontWeight: 600 }}>
                🎉 Use code {service.coupon_code} for{' '}
                {service.coupon_discount_type === 'percent'
                  ? `${parseFloat(service.coupon_discount_value)}% off`
                  : `$${parseFloat(service.coupon_discount_value).toFixed(2)} off`}{' '}
                this session - it'll be applied automatically when you book.
              </div>
            )}
            <div className="flex-between" style={{ background: '#f5f6fa', padding: 16, borderRadius: 12 }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#c2366f' }}>${parseFloat(service.price).toFixed(2)}</div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Price</div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#2563eb' }}>{service.duration_minutes}</div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Minutes</div>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-card">
          {showLogin ? (
            <>
              <h1>Log In</h1>
              <p className="auth-subtitle">Log in to book this session{service.coupon_code ? ' with your coupon' : ''}</p>
              {error && <div className="error-message">{error}</div>}
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    required
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    required
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  />
                </div>
                <button type="submit" disabled={loggingIn} className="btn-auth">
                  {loggingIn ? 'Logging in...' : 'Log In & Continue'}
                </button>
              </form>
              <p>Don't have an account? <a href="#login" onClick={(e) => { e.preventDefault(); setShowLogin(false); setError(null); }}>Sign up instead</a></p>
            </>
          ) : (
            <>
              <h1>Create Your Account</h1>
              <p className="auth-subtitle">Sign up to request this session</p>
              {error && <div className="error-message">{error}</div>}
              <form onSubmit={handleRegisterAndBook}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a secure password"
                  />
                </div>
                <label className="auth-checkbox">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  <span>
                    I agree to the <a href="/legal/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a> and{' '}
                    <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </span>
                </label>
                <button type="submit" disabled={registering || !acceptedTerms} className="btn-auth">
                  {registering ? 'Creating Account...' : 'Create Account & Continue'}
                </button>
              </form>
              <p>Already have an account? <a href="#login" onClick={(e) => { e.preventDefault(); setShowLogin(true); setError(null); }}>Log in instead</a></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShortURLLanding;
